import { coerceBooleanProperty } from '@angular/cdk/coercion';
import {
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnInit,
  Renderer2,
  SimpleChanges,
} from '@angular/core';
import { LayoutService } from '../../services/layout.service';
import { combineLatest } from 'rxjs';

@Component({
  selector: 'ag-page-sticky',
  templateUrl: './page-sticky.component.html',
  styleUrls: ['./page-sticky.component.scss'],
})
/**
 * The PageSticky components helps paging sticky items within a page by adjusting for the current header height.
 * PageSticky must be last child within the parent so it displays on top of everything.
 */
export class PageStickyComponent implements OnInit, OnChanges {
  private _position:
    | 'top-right'
    | 'top-left'
    | 'bottom-right'
    | 'bottom-left'
    | 'top'
    | 'right'
    | 'bottom'
    | 'left' = 'bottom-right';
  hostElement: any;
  @Input()
  get position() {
    return this._position;
  }
  set position(
    value:
      | 'top-right'
      | 'top-left'
      | 'bottom-right'
      | 'bottom-left'
      | 'top'
      | 'right'
      | 'bottom'
      | 'left',
  ) {
    this._position = value || 'bottom-right';
  }
  private _expand = false;
  @Input()
  get expand() {
    return this._expand;
  }
  set expand(value: any) {
    this._expand = coerceBooleanProperty(value);
  }

  /** [y,x] or [t,r,b,l] offset */
  @Input() offset: [number, number] | [number, number, number, number];

  headerHeight: number = 0;
  drawerWidth: number = 0;
  drawerPosition: 'start' | 'end' = null;

  constructor(
    private _renderer: Renderer2,
    private _elementHost: ElementRef,
    public layoutService: LayoutService,
  ) {
    this.hostElement = this._elementHost.nativeElement;
  }

  ngOnInit(): void {
    this._setPositions();

    combineLatest([
      this.layoutService.headerHeight$,
      this.layoutService.drawerWidth$,
      this.layoutService.drawerPosition$,
    ]).subscribe(([heightOffset, drawerWidth, drawerPosition]) => {
      this.headerHeight = heightOffset;
      this.drawerWidth = drawerWidth;
      this.drawerPosition = drawerPosition;
      this._handleLayoutChange(heightOffset, drawerWidth, drawerPosition);
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.offset) {
      this._handleLayoutChange(this.headerHeight, this.drawerWidth, this.drawerPosition);
    }
  }

  private _setPositions() {
    this._elementHost.nativeElement.classList.add('fixed');
    this._elementHost.nativeElement.classList.add(`fixed-${this.position}`);

    if (!this._expand) {
      this._elementHost.nativeElement.classList.add('ag-page-sticky--shrink');
    }
  }

  private _handleLayoutChange(
    heightOffset: number,
    drawerWidth: number,
    drawerPosition: 'start' | 'end',
  ) {
    const leftOffset = drawerPosition === 'start' ? drawerWidth : 0;
    const rightOffset = drawerPosition === 'end' ? drawerWidth : 0;
    this._handleLayoutOffsets(heightOffset, leftOffset, rightOffset);
  }

  /**
   *
   * @param height
   * @returns
   */
  private _handleLayoutOffsets(topOffset: number, leftOffset: number, rightOffset: number) {
    switch (this.position) {
      case 'top':
        this._translate('0px', topOffset + 'px');
        this._renderer.setStyle(this.hostElement, 'left', `${leftOffset}px`);
        this._renderer.setStyle(this.hostElement, 'right', `${rightOffset}px`);
        break;
      case 'bottom':
        this._translate('0px', '0px');
        this._renderer.setStyle(this.hostElement, 'left', `${leftOffset}px`);
        this._renderer.setStyle(this.hostElement, 'right', `${rightOffset}px`);
        break;
      case 'left':
        this._translate(`${leftOffset}px`, '0px');
        this._renderer.setStyle(this.hostElement, 'top', `${topOffset}px`);
        this._renderer.setStyle(this.hostElement, 'bottom', '0px');
        break;
      case 'right':
        this._translate(`-${rightOffset}px`, '0px');
        this._renderer.setStyle(this.hostElement, 'top', `${topOffset}px`);
        this._renderer.setStyle(this.hostElement, 'bottom', '0px');
        break;
      case 'top-left':
        this._translate(`${leftOffset}px`, `${topOffset}px`);
        break;
      case 'top-right':
        this._translate(`-${rightOffset}px`, `${topOffset}px`);
        break;
      case 'bottom-left':
        this._translate(`${leftOffset}px`, '0px');
        break;
      case 'bottom-right':
        this._translate(`-${rightOffset}px`, '0px');
        break;
    }
    if (this.offset) {
      this._renderer.setStyle(
        this.hostElement,
        'margin',
        this.offset.length === 4
          ? `${this.offset[0]}px ${this.offset[1]}px ${this.offset[2]}px ${this.offset[3]}px`
          : `${this.offset[0]}px ${this.offset[1]}px`,
      );
    }
  }

  private _translate(x: string, y: string) {
    this._renderer.setStyle(this.hostElement, 'transform', `translate(${x},${y})`);
  }
}
